export type Attribution = {
  version: number;
  captured_at: string;
  channel?: string | null;
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
  };
  hubspot?: {
    hsa_cam?: string;
    hsa_grp?: string;
    hsa_ad?: string;
    hsa_src?: string;
    hsa_net?: string;
    hsa_kw?: string;
  };
  click_ids?: {
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
    fbclid?: string;
    msclkid?: string;
  };
  landing_page_url?: string;
  referrer?: string | null;
  first_touch?: unknown;
};

const ATTRIBUTION_SESSION_KEY = 'dm_attribution_session';

function getParam(params: URLSearchParams, name: string): string | undefined {
  const val = params.get(name);
  return val || undefined;
}

function deriveChannel(
  referrer: string | null,
  utmSource?: string,
  utmMedium?: string,
  gclid?: string,
  fbclid?: string,
  hsaNet?: string,
  msclkid?: string,
): string | null {
  const source = utmSource?.toLowerCase() || '';
  const medium = utmMedium?.toLowerCase() || '';

  if (source.includes('google') || source.includes('bing') || source === 'adwords') {
    return 'Paid Search';
  }

  if (
    source.includes('facebook') ||
    source.includes('instagram') ||
    source.includes('fb') ||
    source === 'ig'
  ) {
    return 'Paid Social';
  }

  if (gclid) return 'Paid Search';
  if (msclkid) return 'Paid Search';
  if (fbclid) return 'Paid Social';

  if (hsaNet) {
    const net = hsaNet.toLowerCase();
    if (net === 'google' || net === 'bing') return 'Paid Search';
    if (net === 'facebook' || net === 'instagram' || net === 'linkedin') return 'Paid Social';
    return 'Paid';
  }

  if (medium === 'cpc' || medium === 'ppc' || medium === 'paid' || medium === 'pmax') {
    return 'Paid Search';
  }
  if (medium === 'paid_social' || medium === 'paidsocial') {
    return 'Paid Social';
  }
  if (medium === 'email') {
    return 'Email';
  }

  if (!referrer) return 'Direct';

  try {
    const refHost = new URL(referrer).hostname.toLowerCase();

    const ownDomains = [
      'thisisdelmar.com',
      'book.thisisdelmar.com',
      'book-staging.thisisdelmar.com',
    ];
    if (ownDomains.some((d) => refHost === d || refHost.endsWith('.' + d))) {
      return 'Direct';
    }

    const searchEngines = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'baidu.'];
    const socialNetworks = [
      'facebook.',
      'instagram.',
      'twitter.',
      'linkedin.',
      'tiktok.',
      'pinterest.',
      'youtube.',
    ];

    if (searchEngines.some((se) => refHost.includes(se))) return 'Organic Search';
    if (socialNetworks.some((sn) => refHost.includes(sn))) return 'Organic Social';
    if (utmMedium?.toLowerCase() === 'email') return 'Email';
    return 'Referral';
  } catch {
    return 'Referral';
  }
}

function deriveSource(referrer: string | null, utmSource?: string, hsaNet?: string): string | null {
  if (utmSource) return utmSource;
  if (hsaNet) return hsaNet;
  if (!referrer) return null;

  try {
    return new URL(referrer).hostname.replace('www.', '');
  } catch {
    return null;
  }
}

function deriveMedium(
  utmMedium?: string,
  gclid?: string,
  fbclid?: string,
  msclkid?: string,
): string | null {
  if (utmMedium) return utmMedium;
  if (gclid) return 'cpc';
  if (msclkid) return 'cpc';
  if (fbclid) return 'paid_social';
  return null;
}

function getLegacyAttribution(): string | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const sourceParam = params.get('source');
  if (sourceParam) return sourceParam;

  try {
    return localStorage.getItem('dm-attribution');
  } catch {
    return null;
  }
}

function captureAttribution(): Attribution {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || null;

  const utmSource = getParam(params, 'utm_source');
  const utmMedium = getParam(params, 'utm_medium');
  const utmCampaign = getParam(params, 'utm_campaign');
  const utmTerm = getParam(params, 'utm_term');
  const utmContent = getParam(params, 'utm_content');

  const hsaCam = getParam(params, 'hsa_cam');
  const hsaGrp = getParam(params, 'hsa_grp');
  const hsaAd = getParam(params, 'hsa_ad');
  const hsaSrc = getParam(params, 'hsa_src');
  const hsaNet = getParam(params, 'hsa_net');
  const hsaKw = getParam(params, 'hsa_kw');

  const gclid = getParam(params, 'gclid');
  const gbraid = getParam(params, 'gbraid');
  const wbraid = getParam(params, 'wbraid');
  const fbclid = getParam(params, 'fbclid');
  const msclkid = getParam(params, 'msclkid');

  const googleClick = gclid || gbraid || wbraid;
  const channel = deriveChannel(
    referrer,
    utmSource,
    utmMedium,
    googleClick,
    fbclid,
    hsaNet,
    msclkid,
  );
  let source = deriveSource(referrer, utmSource, hsaNet);
  const medium = deriveMedium(utmMedium, googleClick, fbclid, msclkid);

  // If channel is Direct, check for legacy dm-attribution and use as source
  if (channel === 'Direct') {
    const legacyAttribution = getLegacyAttribution();
    if (legacyAttribution) {
      source = legacyAttribution;
    }
  }

  const hasUtm = utmSource || utmMedium || utmCampaign || utmTerm || utmContent;
  const hasHubspot = hsaCam || hsaGrp || hsaAd || hsaSrc || hsaNet || hsaKw;
  const hasClickIds = googleClick || fbclid || msclkid;

  return {
    version: 1,
    captured_at: new Date().toISOString(),
    channel,
    source,
    medium,
    campaign: utmCampaign || null,
    ...(hasUtm && {
      utm: {
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
        term: utmTerm,
        content: utmContent,
      },
    }),
    ...(hasHubspot && {
      hubspot: {
        hsa_cam: hsaCam,
        hsa_grp: hsaGrp,
        hsa_ad: hsaAd,
        hsa_src: hsaSrc,
        hsa_net: hsaNet,
        hsa_kw: hsaKw,
      },
    }),
    ...(hasClickIds && {
      click_ids: {
        gclid,
        gbraid,
        wbraid,
        fbclid,
        msclkid,
      },
    }),
    landing_page_url: window.location.href,
    referrer,
  };
}

export function readAttributionCookie(): Attribution | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  let incoming: Attribution | null = null;
  try {
    const dmAttr = params.get('dm_attr');
    if (dmAttr) {
      const parsed = JSON.parse(dmAttr);
      if (parsed && typeof parsed === 'object') {
        // Use footer's source param as fallback only if dm_attr doesn't already have a source
        const sourceParam = params.get('source');
        if (sourceParam && !parsed.source) {
          parsed.source = sourceParam;
        }

        incoming = parsed as Attribution;
      }
    }
  } catch {}

  let stored: Attribution | null = null;
  try {
    const sessionData = sessionStorage.getItem(ATTRIBUTION_SESSION_KEY);
    if (sessionData) {
      const parsed = JSON.parse(sessionData);
      if (parsed && typeof parsed === 'object') {
        stored = parsed as Attribution;
      }
    }
  } catch {}

  const hasFreshClick = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'].some((key) =>
    params.get(key)?.trim(),
  );
  // A new click describes the current visit, while the marketing first-touch envelope stays intact.
  const firstTouch = incoming?.first_touch ?? stored?.first_touch;
  const attribution = hasFreshClick
    ? { ...captureAttribution(), ...(firstTouch && { first_touch: firstTouch }) }
    : incoming && (!stored || Date.parse(incoming.captured_at) > Date.parse(stored.captured_at))
      ? incoming
      : stored || incoming || captureAttribution();
  try {
    sessionStorage.setItem(ATTRIBUTION_SESSION_KEY, JSON.stringify(attribution));
  } catch {}
  return attribution;
}

let initializedAttributionUrl: string | null = null;

export function initAttribution(): void {
  if (typeof window === 'undefined') return;
  if (initializedAttributionUrl === window.location.href) return;
  initializedAttributionUrl = window.location.href;

  // Capture source param to localStorage (legacy attribution)
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    if (source) {
      localStorage.setItem('dm-attribution', source);
    }
  } catch {}

  readAttributionCookie();
}

export function getAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null;

  try {
    const sessionData = sessionStorage.getItem(ATTRIBUTION_SESSION_KEY);
    if (sessionData) {
      return JSON.parse(sessionData) as Attribution;
    }
  } catch {}
  return null;
}
